import clsx from "clsx";
import { Monitor, Moon, Sun } from "lucide-react";
import { useMe } from "../lib/queries";
import { type Theme, useSetTheme } from "../lib/theme";

const themeOptions: { value: Theme; label: string; icon: typeof Sun }[] = [
    { value: "system", label: "Device", icon: Monitor },
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
];

/**
 * Device (follows the phone or computer's own setting) / Light / Dark, saved to the user's
 * account (lib/theme.ts). Settings shows each with a small picture of the app in it; `compact`
 * (the account menu) is just the buttons.
 */
export function ThemePicker({ compact = false }: Readonly<{ compact?: boolean }>) {
    const { data: me } = useMe();
    const setTheme = useSetTheme();
    const current = me?.user.theme ?? "system";
    return (
        <div className={clsx("grid grid-cols-3", compact ? "gap-1" : "gap-3")} role="radiogroup" aria-label="Theme">
            {themeOptions.map(({ value, label, icon: ThemeIcon }) => {
                const on = current === value;
                return (
                    <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => setTheme.mutate(value)}
                        className={clsx(
                            "flex cursor-pointer flex-col overflow-hidden rounded-md border-2 transition",
                            compact ? "items-center gap-0.5 px-2 py-1.5 text-xs" : "text-sm",
                            on ? "border-brand-500 font-semibold text-brand-ink" : "border-line text-muted hover:border-brand-500/50 hover:text-ink",
                            on && compact && "bg-brand-500/10",
                        )}
                    >
                        {compact ? null : <ThemePreview theme={value} />}
                        <span className={clsx(compact ? "flex flex-col items-center gap-0.5" : "flex items-center justify-center gap-1.5 px-3 py-2", on && !compact && "bg-brand-500/10")}>
                            <ThemeIcon className="size-4" />
                            {label}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

/**
 * A tiny picture of the app in a theme: a window's title bar, a heading and two lines of text.
 * Its colours are the themes' own (not the current one's), so each shows as it would look.
 * Device is half of each.
 */
function ThemePreview({ theme }: Readonly<{ theme: Theme }>) {
    if (theme === "system") {
        return (
            <span className="grid h-16 grid-cols-2 border-b border-line" aria-hidden="true">
                <MiniApp dark={false} />
                <MiniApp dark />
            </span>
        );
    }
    return (
        <span className="grid h-16 border-b border-line" aria-hidden="true">
            <MiniApp dark={theme === "dark"} />
        </span>
    );
}

function MiniApp({ dark }: Readonly<{ dark: boolean }>) {
    const line = dark ? "bg-white/20" : "bg-black/10";
    return (
        <span className={clsx("flex flex-col gap-1.5 p-2", dark ? "bg-[#1b1c1e]" : "bg-[#f4f5f5]")}>
            <span className="h-2 w-full rounded-sm bg-window" />
            <span className={clsx("h-1.5 w-1/2 rounded-sm", dark ? "bg-white/40" : "bg-black/25")} />
            <span className={clsx("h-1 w-5/6 rounded-sm", line)} />
            <span className={clsx("h-1 w-2/3 rounded-sm", line)} />
        </span>
    );
}
